/**
 * The redirect map — `/v1/admin/cms/redirects` (E51).
 *
 * Most of these rows were not typed here. Archiving a CMS page takes a redirect decision and
 * writes the rule in the same transaction, which is why the endpoints live on the CMS router and
 * why this screen is a *reader* first: it exists so somebody can find the rule that is wrong.
 *
 * ⛔ **An unmeasured hit count reads "not measured", never 0.** Since E60 a nightly job
 * (`workers/tasks/seo.py` `redirect_hits`) reads Cloudflare edge analytics and writes
 * `hit_count` with the window it covers. Until a rule's first good read the API serves `null`,
 * and this screen says so where the number would be: a map reading "0 hits" is the evidence
 * somebody deletes rules on, and an unmeasured rule may be carrying the most traffic on the site.
 * A measured count always carries its window, because "12 hits" means nothing without "in 30
 * days".
 *
 * ⛔ **There is no delete, only on/off.** An indexed URL's history is the thing the map is for. A
 * rule that was wrong is still the record of what that URL used to do.
 *
 * ⛔ **Every refusal is the server's and is shown verbatim.** The duplicate names the rule that
 * already answers for that URL; both chain refusals — forward and backward — name the rule to
 * fix; the path refusals say which side is misspelled. The old fixture version of this screen
 * pre-judged all of them and silently normalised trailing slashes as you typed. That rule now
 * lives in `services/cms.assert_redirect_path`, because a rule that lives only in the client is
 * one build away from being switched off.
 */
import { useState } from 'react';
import { Card, Icon, Kicker, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { ConfirmAction, Consequences, FilterBar, ScopeAction, StatTile, REFUSED } from '../components/kit';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useCreateRedirect,
  useRedirects,
  useSetRedirectActive,
  type RedirectRule,
} from '../lib/api/hooks/cms';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';
import { formatDate, formatDateTime } from '../lib/datetime';

export type RedirectKind = '301' | '302' | '410';

/** The three the server accepts. A 404 is the absence of a decision, so it is not offered. */
export const KINDS: readonly RedirectKind[] = ['301', '302', '410'];

export const KIND_TONE: Readonly<Record<RedirectKind, 'success' | 'info' | 'error'>> = {
  '301': 'success',
  '302': 'info',
  '410': 'error',
};

export const KIND_MEANS: Readonly<Record<RedirectKind, string>> = {
  '301': 'Permanent — search engines move the ranking to the target',
  '302': 'Temporary — the original keeps its ranking',
  '410': 'Gone — the page is deliberately removed, not moved',
};

/**
 * ⛔ The sentence that stands where a hit count would.
 *
 * Written once and shown everywhere the number is absent, so the screen cannot end up saying
 * "not measured" in one place and drawing a zero in another.
 */
export const HITS_UNMEASURED = 'not measured';

export const HITS_WHY =
  'No hit count has been read yet. A nightly job counts each rule from Cloudflare edge ' +
  'analytics; until its first good read, treat every rule here as load-bearing — a rule with no ' +
  'measurement is not a rule with no traffic.';

/** The banner once counts exist: what they cover and how fresh they are. */
export function hitsNote(rules: RedirectRule[]): string | null {
  const measured = rules.filter((r) => r.hit_count != null && r.hit_count_window_days != null);
  if (measured.length === 0) return null;
  const days = Math.max(...measured.map((r) => r.hit_count_window_days as number));
  return (
    `Hits are edge requests in the last ${days} days, counted nightly from Cloudflare ` +
    `analytics. A rule reading “${HITS_UNMEASURED}” has not had a good read yet — not zero traffic.`
  );
}

/** One rule's count with its window, or the sentence that stands where it would be. */
export function hitsLabel(rule: RedirectRule): string {
  if (rule.hit_count == null || rule.hit_count_window_days == null) return HITS_UNMEASURED;
  return `${rule.hit_count.toLocaleString('en-IN')} in ${rule.hit_count_window_days} days`;
}

/** `http_status` is a number on the wire and a tab key here. One conversion, in one place. */
export function kindOf(rule: RedirectRule): RedirectKind {
  return String(rule.http_status) as RedirectKind;
}

/**
 * The tab filter.
 *
 * ⛔ Client-side deliberately, and safe only because the read is unpaginated — the whole point of
 * the screen is to scan the map rather than page through it. It is *not* safe past the server's
 * cap, which is why the response carries `total` and the screen says so when the two differ.
 */
export function redirectFilter(tab: string, rule: RedirectRule): boolean {
  if (tab === 'off') return !rule.is_active;
  if (tab === 'all') return true;
  return kindOf(rule) === tab;
}

/** Search over everything an operator would type: either path, or the note explaining the rule. */
export function matchesQuery(rule: RedirectRule, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (needle === '') return true;
  return `${rule.from_path} ${rule.to_path} ${rule.note}`.toLowerCase().includes(needle);
}

/**
 * What the screen says when it is holding part of the table.
 *
 * ⛔ Returns null when it holds all of it — a permanent "showing all 12 rules" line is furniture
 * nobody reads, and the point of the sentence is that it appears exactly when the filter above it
 * has stopped being able to answer "is there a rule for this URL?".
 */
export function capNote(shown: number, total: number): string | null {
  if (total <= shown) return null;
  return `Showing the newest ${shown} of ${total} rules. Filtering and search look only at these — a rule older than the ${shown}th will not appear here even if it exists.`;
}

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same tab. */
const TAB_KEYS: readonly string[] = ['all', '301', '302', '410', 'off'];

export function Redirects() {
  const { scope, toast } = useConsole();
  const [q, setQ] = useUrlSearch('redirects');
  const [tab, setTab] = useUrlTab('redirects', TAB_KEYS, 'all');
  const [confirm, setConfirm] = useState<'new' | 'toggle' | null>(null);
  const [fromPath, setFromPath] = useState('');
  const [toPath, setToPath] = useState('');
  const [note, setNote] = useState('');
  const [kind, setKind] = useState<RedirectKind>('301');

  const list = useRedirects();
  const create = useCreateRedirect();
  const setActive = useSetRedirectActive();

  const items = list.data?.items ?? [];
  const rows = items.filter((r) => redirectFilter(tab, r) && matchesQuery(r, q));
  const { focus, setFocus, open: focused, setOpen } = useQueue(rows, { key: tab + q });
  /* The queue holds WHICH row is open; the list holds what that row currently says. Reading the
     detail off the queue's own copy would leave it showing the pre-toggle state after a write,
     because the refetch replaces the row in `items` and not the one the queue captured. */
  const open = items.find((r) => r.id === focused?.id) ?? null;
  const cap = capNote(items.length, list.data?.total ?? 0);

  const closeForm = () => {
    setConfirm(null);
    setFromPath('');
    setToPath('');
    setNote('');
    create.reset();
  };

  const kindCount = (k: RedirectKind) => items.filter((r) => kindOf(r) === k).length;
  const needsTarget = kind !== '410';

  const newDialog = (
    <ConfirmAction
      open={confirm === 'new'}
      title="Create a redirect?"
      phrase="CREATE"
      tone="warn"
      reason
      consequences={[
        needsTarget && fromPath && toPath
          ? `Sends ${fromPath} to ${toPath} as a ${kind} from the next build.`
          : `Answers ${fromPath || 'the source path'} with a ${kind} from the next build.`,
        KIND_MEANS[kind],
        'Rules take effect on the next site build, not immediately.',
      ]}
      onCancel={closeForm}
      onConfirm={(reason) =>
        create.mutate(
          {
            from_path: fromPath,
            to_path: needsTarget ? toPath : '',
            http_status: Number(kind),
            note,
            reason,
          },
          {
            onSuccess: () => {
              closeForm();
              toast({ tone: 'success', message: 'Redirect created · live at the next build' });
            },
            /* ⛔ The dialog STAYS OPEN on a refusal. Every one of them names a rule to go and
               fix — the duplicate names the rule that already answers for that URL, both chain
               refusals name the rule in the way — and a toast that replaces the form takes that
               sentence away at the moment it is needed. */
          },
        )
      }
      confirmLabel={create.isPending ? 'Creating…' : 'Create redirect'}
    >
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 120px', gap: 'var(--sp-md)' }}>
          <Input
            density="admin"
            label="From path"
            value={fromPath}
            onChange={setFromPath}
            placeholder="/puja/old-slug/patna"
            helper="The path visitors still arrive on, exactly as it is indexed — including its capitals."
          />
          <Select
            density="admin"
            label="Kind"
            value={kind}
            onChange={(v) => setKind(v as RedirectKind)}
            options={[...KINDS]}
          />
        </div>

        {needsTarget ? (
          <Input
            density="admin"
            label="Target path"
            value={toPath}
            onChange={setToPath}
            placeholder="/puja/new-slug/patna"
            helper={KIND_MEANS[kind]}
          />
        ) : (
          <div
            style={{
              background: 'var(--c-error-bg)',
              border: '1px solid var(--c-error)',
              borderRadius: 'var(--r-tile)',
              padding: 'var(--sp-md)',
              fontSize: 'var(--fs-admin-cell)',
              color: 'var(--c-error-text)',
            }}
          >
            A 410 has no target — the row stores none. Use it only when the page is deliberately
            gone; for a page that moved, a 301 keeps the ranking.
          </div>
        )}

        <Input
          density="admin"
          label="Why"
          value={note}
          onChange={setNote}
          placeholder="Danapur folded into the Patna service area"
          helper="Recorded on the rule. In a year this is the only thing that explains it."
        />

        {create.isError && (
          /* ⛔ Verbatim. The server's refusal is the one that knows which rule is in the way. */
          <Consequences tone="error" kicker={REFUSED} items={[errorMessage(create.error)]} />
        )}
      </div>
    </ConfirmAction>
  );

  const toggleDialog = open && (
    <ConfirmAction
      open={confirm === 'toggle'}
      title={open.is_active ? 'Switch this redirect off?' : 'Switch this redirect back on?'}
      phrase={open.is_active ? 'SWITCH OFF' : 'SWITCH ON'}
      tone={open.is_active ? 'error' : 'warn'}
      reason
      consequences={
        open.is_active
          ? [
              `${open.from_path} stops redirecting at the next build and starts returning 404.`,
              'The rule is kept, not deleted — it stays here as the record of what that URL used to do, and can be switched back on.',
              'A switched-off rule no longer blocks a replacement, so the path can be pointed somewhere new.',
            ]
          : [
              `${open.from_path} answers with a ${open.http_status} again from the next build.`,
              'If another rule now points at this path, the server will refuse and name it.',
            ]
      }
      onCancel={() => setConfirm(null)}
      onConfirm={(reason) =>
        setActive.mutate(
          { id: open.id, isActive: !open.is_active, reason },
          {
            onSuccess: () => {
              setConfirm(null);
              toast({
                tone: open.is_active ? 'error' : 'success',
                message: `Rule ${open.is_active ? 'switched off' : 'switched on'} · effective at the next build`,
              });
            },
            onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
          },
        )
      }
      confirmLabel={setActive.isPending ? 'Saving…' : open.is_active ? 'Switch off' : 'Switch on'}
    />
  );

  if (open) {
    const openKind = kindOf(open);
    return (
      <DetailPage
        title={open.from_path}
        code={`${open.http_status} → ${openKind === '410' ? 'gone' : open.to_path}`}
        status={
          <StatusChip
            tone={open.is_active ? KIND_TONE[openKind] : 'neutral'}
            label={open.is_active ? String(open.http_status) : `${open.http_status} · off`}
          />
        }
        onClose={() => setOpen(null)}
        audit={`Created ${formatDateTime(open.created_at)} · hits: ${hitsLabel(open)}${
          open.hit_counted_at ? ` (counted ${formatDate(open.hit_counted_at)})` : ''
        }`}
        actions={
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant={open.is_active ? 'destructive' : 'secondary'}
            onClick={() => setConfirm('toggle')}
          >
            {open.is_active ? 'Switch off' : 'Switch on'}
          </ScopeAction>
        }
      >
        <KV
          rows={[
            ['From', open.from_path],
            [
              'Target',
              openKind === '410' ? (
                <span key="t" style={{ color: 'var(--c-ink3)' }}>
                  none — the page is gone, not moved
                </span>
              ) : (
                open.to_path
              ),
            ],
            ['Kind', <StatusChip key="k" tone={KIND_TONE[openKind]} label={String(open.http_status)} />],
            ['What that means', KIND_MEANS[openKind]],
            ['Serving', open.is_active ? 'Yes — live at the next build' : 'No — switched off'],
            [
              'Why',
              open.note || (
                <span key="n" style={{ color: 'var(--c-ink3)' }}>
                  no reason recorded
                </span>
              ),
            ],
            [
              'Hits',
              <span key="h" style={{ color: 'var(--c-ink3)' }}>
                {HITS_UNMEASURED}
              </span>,
            ],
            ['Created', formatDateTime(open.created_at)],
          ]}
        />

        <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
          <Kicker>Before you switch this off</Kicker>
          <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
            {HITS_WHY}
          </p>
        </div>

        <Consequences
          tone="warn"
          items={[
            'There is no delete here on purpose. A rule that was wrong is still the record of what an indexed URL used to do.',
            'If the target itself moves, switch this rule off and add one straight to the new target rather than a second hop — the server refuses chains in both directions and names the rule in the way.',
          ]}
        />
        {toggleDialog}
      </DetailPage>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: 'var(--c-sunk)',
          border: '1px solid var(--c-border)',
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
          color: 'var(--c-ink2)',
          flexWrap: 'wrap',
        }}
      >
        <Icon name="signpost" size={16} />
        <span style={{ flex: 1, minWidth: 280 }}>
          Most rules here were written by archiving a page, not typed — archiving takes a redirect
          decision and writes the rule in the same transaction. Rules take effect on the next site
          build.
        </span>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          icon="plus"
          onClick={() => setConfirm('new')}
        >
          New redirect
        </ScopeAction>
      </div>

      {list.isPending && <LoadingState message="Reading the redirect map…" />}
      {list.isError && (
        <ErrorState title="Could not read the redirect map" cause={errorMessage(list.error)} />
      )}

      {list.isSuccess && items.length === 0 && (
        <Card variant="flat" padding="var(--sp-xxl)">
          <EmptyState
            icon="signpost"
            title="No redirect rules yet"
            body="Rules appear when a page is archived — archiving takes the redirect decision with it. An empty map means nothing has been retired, not that redirects are switched off."
            action={
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                icon="plus"
                onClick={() => setConfirm('new')}
              >
                New redirect
              </ScopeAction>
            }
          />
        </Card>
      )}

      {list.isSuccess && items.length > 0 && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
              gap: 'var(--sp-md)',
            }}
          >
            <StatTile label="Rules" value={items.length} note="301, 302 and 410 combined" />
            <StatTile
              label="Serving"
              value={items.filter((r) => r.is_active).length}
              note="live at the next build"
            />
            <StatTile
              label="Switched off"
              value={items.filter((r) => !r.is_active).length}
              note="kept as history, not serving"
            />
            <StatTile label="410 — gone" value={kindCount('410')} note="deliberate removals" tone="error" />
          </div>

          {/* ⛔ What the hit column covers, or why it is empty — on screen, not in a comment. */}
          <Card variant="flat" padding="var(--sp-lg)">
            <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'start' }}>
              <Icon name="triangle-alert" size={16} style={{ color: 'var(--c-warn)', flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
                {hitsNote(items) ?? (
                  <>
                    <strong>Hits are {HITS_UNMEASURED}.</strong> {HITS_WHY}
                  </>
                )}
              </span>
            </div>
          </Card>

          {cap && (
            <Card variant="flat" padding="var(--sp-md) var(--sp-lg)">
              <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>{cap}</span>
            </Card>
          )}

          <FilterBar
            q={q}
            onQ={setQ}
            placeholder="From path, target, reason — press /"
            tab={tab}
            onTab={setTab}
            tabs={[
              { key: 'all', label: `All · ${items.length}` },
              { key: '301', label: `Permanent 301 · ${kindCount('301')}` },
              { key: '302', label: `Temporary 302 · ${kindCount('302')}` },
              { key: '410', label: `Gone 410 · ${kindCount('410')}` },
              { key: 'off', label: `Switched off · ${items.filter((r) => !r.is_active).length}` },
            ]}
            right={
              <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                Archiving writes its own rule — most rows here arrived that way
              </span>
            }
          />

          {rows.length > 0 ? (
            <DataTable
              density="comfortable"
              focusedIndex={focus}
              onFocusRow={setFocus}
              onRowClick={(row) => setOpen(rows.find((r) => r.id === row.id)!)}
              footer={
                <>
                  <span>
                    {rows.length} of {items.length} rules
                  </span>
                  <span>{QUEUE_HINT}</span>
                </>
              }
              columns={[
                { key: 'from', label: 'From path', mono: true },
                { key: 'kind', label: 'Kind' },
                { key: 'to', label: 'Target', mono: true },
                { key: 'note', label: 'Why' },
                { key: 'created', label: 'Created' },
                { key: 'hits', label: 'Hits', align: 'right' },
              ]}
              rows={rows.map((r) => ({
                id: r.id,
                from: r.from_path,
                kind: (
                  <StatusChip
                    tone={r.is_active ? KIND_TONE[kindOf(r)] : 'neutral'}
                    label={r.is_active ? String(r.http_status) : `${r.http_status} · off`}
                  />
                ),
                to:
                  kindOf(r) === '410' ? (
                    <span style={{ color: 'var(--c-ink3)' }}>no target — gone</span>
                  ) : (
                    r.to_path
                  ),
                note: r.note || <span style={{ color: 'var(--c-ink3)' }}>—</span>,
                created: formatDate(r.created_at),
                /* ⛔ A count only with its window; unmeasured is words, never a zero. */
                hits:
                  r.hit_count == null ? (
                    <span style={{ color: 'var(--c-ink3)' }}>{HITS_UNMEASURED}</span>
                  ) : (
                    hitsLabel(r)
                  ),
              }))}
            />
          ) : (
            <Card variant="flat" padding="var(--sp-xxl)">
              <EmptyState
                icon="signpost"
                title="No redirect matches this filter"
                body="Clear the search or pick another tab. If you were looking for a specific URL and it is not here, the rule may simply not exist — archiving writes one, a manual rule does not appear until it is created."
              />
            </Card>
          )}
        </>
      )}

      {newDialog}
    </>
  );
}
